'use client'

import { useState, useEffect, useRef } from 'react'
import { ClinicaService, EstadoItem, CidadeItem, BairroItem, ClinicaItem } from '../../lib/services'
import { FormMessage } from '../../lib/components'
import { trackClarityEvent } from '@/lib/clarity'

const normalizeString = (str: string) => {
  if (!str) return '';
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

// Componente de Select com Busca
export interface SearchableSelectProps<T> {
  options: T[]
  value: string
  onChange: (value: string) => void
  getOptionValue: (option: T) => string
  getOptionLabel: (option: T) => string
  placeholder: string
  disabled?: boolean
  loading?: boolean
  emptyMessage?: string
  onNoResults?: (data: { searchTerm: string; field: string; currentValue: string; allFilters: Record<string, string> }) => void
  fieldName?: string
  allFilters?: Record<string, string>
  allowClear?: boolean
}

export function SearchableSelect<T>({
  options,
  value,
  onChange,
  getOptionValue,
  getOptionLabel,
  placeholder,
  disabled = false,
  loading = false,
  emptyMessage = 'Nenhuma opção encontrada',
  onNoResults,
  fieldName = '',
  allFilters = {},
  allowClear = false
}: SearchableSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const previousSearchTermRef = useRef<string>('')

  // Fechar quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
        previousSearchTermRef.current = ''
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filtrar opções baseado no termo de busca
  const filteredOptions = options.filter(option => {
    const label = getOptionLabel(option)
    return label && normalizeString(label).includes(normalizeString(searchTerm))
  })

  // Disparar evento quando não houver resultados e houver termo de busca
  useEffect(() => {
    if (
      isOpen &&
      searchTerm.length > 0 &&
      filteredOptions.length === 0 &&
      options.length > 0 &&
      searchTerm !== previousSearchTermRef.current &&
      onNoResults &&
      fieldName
    ) {
      // Só disparar se realmente não encontrou resultados (não é lista vazia inicial)
      previousSearchTermRef.current = searchTerm
      onNoResults({
        searchTerm,
        field: fieldName,
        currentValue: value,
        allFilters
      })
    }
  }, [searchTerm, filteredOptions.length, isOpen, options.length, onNoResults, fieldName, value, allFilters])

  // Obter label do valor selecionado
  const selectedOption = options.find(opt => getOptionValue(opt) === value)
  const displayValue = selectedOption ? getOptionLabel(selectedOption) : ''

  const handleSelect = (optionValue: string) => {
    onChange(optionValue)
    setIsOpen(false)
    setSearchTerm('')
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Input que simula o select */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
          disabled={disabled || loading}
          className={`
            w-full px-4 py-3 border border-gray-300 rounded-lg 
            focus:ring-2 focus:ring-green-500 focus:border-green-500 
            bg-white text-gray-900 text-left
            disabled:bg-gray-100 disabled:cursor-not-allowed
            flex items-center justify-between
            ${allowClear && value && !disabled ? 'pr-14' : ''}
          `}
        >
          <span className={value ? 'text-gray-900' : 'text-gray-500'}>
            {loading ? 'Carregando...' : (value ? displayValue : placeholder)}
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {/* Botão de limpar posicionado absolutamente */}
        {allowClear && value && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onChange('')
              setIsOpen(false)
              setSearchTerm('')
            }}
            className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors z-10"
            title="Limpar seleção"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Input de busca */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar..."
                className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm bg-white text-gray-900"
                autoFocus
              />
              <svg
                className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Lista de opções */}
          <div className="overflow-y-auto max-h-48">
            {/* Opção "Todos" quando allowClear está ativo */}
            {allowClear && (
              <button
                type="button"
                onClick={() => handleSelect('')}
                className={`
                  w-full px-4 py-2 text-left text-sm hover:bg-green-50 transition-colors border-b border-gray-200
                  ${!value ? 'bg-green-100 text-green-900 font-medium' : 'text-gray-900'}
                `}
              >
                Todos
              </button>
            )}
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                {emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const optionValue = getOptionValue(option)
                const optionLabel = getOptionLabel(option)
                const isSelected = value === optionValue

                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelect(optionValue)}
                    className={`
                      w-full px-4 py-2 text-left text-sm hover:bg-green-50 transition-colors
                      ${isSelected ? 'bg-green-100 text-green-900 font-medium' : 'text-gray-900'}
                    `}
                  >
                    {optionLabel}
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}

interface ClinicaSearchProps {
  onClinicaSelect?: (clinica: ClinicaItem) => void
  selectedClinica?: ClinicaItem | null
  disabled?: boolean
  forceClinicaId?: number
  modeloExame?: string // '1' = online, outros = presencial
}

export default function ClinicaSearch({ onClinicaSelect, selectedClinica, disabled = false, forceClinicaId, modeloExame }: ClinicaSearchProps) {
  const [estados, setEstados] = useState<EstadoItem[]>([])
  const [cidades, setCidades] = useState<CidadeItem[]>([])
  const [bairros, setBairros] = useState<BairroItem[]>([])
  const [clinicas, setClinicas] = useState<ClinicaItem[]>([])

  const [selectedEstado, setSelectedEstado] = useState('')
  const [selectedCidade, setSelectedCidade] = useState('')
  const [selectedBairro, setSelectedBairro] = useState('')
  const [selectedClinicaId, setSelectedClinicaId] = useState<number | null>(null)
  const [filtrosInicializados, setFiltrosInicializados] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const [loading, setLoading] = useState({
    estados: false,
    cidades: false,
    bairros: false,
    clinicas: false,
  })

  const [error, setError] = useState('')

  // Carregar estados ao montar componente
  useEffect(() => {
    const loadEstados = async () => {
      setLoading(prev => ({ ...prev, estados: true }))
      try {
        const estadosData = await ClinicaService.getEstados()
        setEstados(estadosData)
      } catch (error: any) {
        console.error('Erro ao carregar estados:', error)
        setError('Erro ao carregar estados')
      } finally {
        setLoading(prev => ({ ...prev, estados: false }))
      }
    }

    loadEstados()
  }, [])

  // Carregar cidades quando estado for selecionado
  useEffect(() => {
    if (!selectedEstado) {
      setCidades([])
      setSelectedCidade('')
      return
    }

    const loadCidades = async () => {
      setLoading(prev => ({ ...prev, cidades: true }))
      try {
        const cidadesData = await ClinicaService.getCidadesByEstado(selectedEstado)
        setCidades(cidadesData)
      } catch (error: any) {
        console.error('Erro ao carregar cidades:', error)
        setError('Erro ao carregar cidades')
      } finally {
        setLoading(prev => ({ ...prev, cidades: false }))
      }
    }

    loadCidades()
  }, [selectedEstado])

  // Carregar bairros quando cidade for selecionada
  useEffect(() => {
    if (!selectedEstado || !selectedCidade) {
      setBairros([])
      setSelectedBairro('')
      return
    }

    const loadBairros = async () => {
      setLoading(prev => ({ ...prev, bairros: true }))
      try {
        console.log(`🏘️ Buscando bairros para ${selectedCidade} - ${selectedEstado}`)
        const bairrosData = await ClinicaService.getBairrosByCidadeEstado(selectedEstado, selectedCidade)
        console.log(`🏘️ Bairros encontrados: ${bairrosData.length}`)
        setBairros(bairrosData)
      } catch (error: any) {
        console.error('Erro ao carregar bairros:', error)
        setError('Erro ao carregar bairros')
      } finally {
        setLoading(prev => ({ ...prev, bairros: false }))
      }
    }

    loadBairros()
  }, [selectedEstado, selectedCidade])

  // Mostrar clínica selecionada se fornecida via prop e preencher filtros
  useEffect(() => {
    if (selectedClinica) {
      // Se a clínica selecionada mudou ou é a primeira vez, atualizar filtros
      if (selectedClinica.id !== selectedClinicaId) {
        setSelectedClinicaId(selectedClinica.id)

        // Preencher filtros apenas se ainda não foram inicializados (primeira vez)
        if (!filtrosInicializados) {
          if (selectedClinica.uf) setSelectedEstado(selectedClinica.uf)
          if (selectedClinica.cidade) setSelectedCidade(selectedClinica.cidade)
          if (selectedClinica.bairro) setSelectedBairro(selectedClinica.bairro)
          setFiltrosInicializados(true)
        }

        // Se a lista estiver vazia, adicionar a clínica selecionada
        if (clinicas.length === 0) {
          setClinicas([selectedClinica])
        }
      }
    }
  }, [selectedClinica, selectedClinicaId, clinicas.length, filtrosInicializados])

  // Limpar filtros quando for exame online
  useEffect(() => {
    if (modeloExame === '1') {
      setSelectedEstado('')
      setSelectedCidade('')
      setSelectedBairro('')
      // Não limpamos as clínicas aqui, pois o outro useEffect vai carregar as online
    }
  }, [modeloExame])

  // Carregar clínicas quando filtros forem aplicados
  useEffect(() => {
    const loadClinicas = async () => {
      setLoading(prev => ({ ...prev, clinicas: true }))
      try {
        let clinicasData: ClinicaItem[] = []

        // Se forceClinicaId estiver definido, buscar apenas essa clínica (não precisa de filtros)
        if (forceClinicaId) {
          try {
            const clinicaForcada = await ClinicaService.getClinicaById(forceClinicaId)
            clinicasData = [clinicaForcada]
            console.log('🔒 [ClinicaSearch] Forçando exibição apenas da clínica:', clinicaForcada.nome)
          } catch (error) {
            console.error('❌ [ClinicaSearch] Erro ao buscar clínica forçada:', error)
            clinicasData = []
          }
        } else {
          // Buscar clínicas normalmente com os filtros (precisa de selectedEstado, exceto para Online)
          const isOnline = modeloExame === '1'

          if (!selectedEstado && !isOnline) {
            setClinicas([])
            return
          }

          // Passar limit alto quando buscar por estado ou cidade para garantir todas as clínicas
          // Backend já aplica limite automático, mas deixamos explícito aqui
          const limit = selectedBairro ? undefined : 10000 // Bairro: deixa backend decidir, Estado/Cidade: 10000

          clinicasData = await ClinicaService.getClinicasFiltradas({
            estado: selectedEstado || undefined,
            cidade: selectedCidade || undefined,
            bairro: selectedBairro || undefined,
            limit,
            online: isOnline ? true : undefined,
          })
        }

        setClinicas(clinicasData)

        // Disparar evento do Clarity caso não encontre clínicas para os filtros fornecidos
        // Isso cobre tanto a navegação via URL quanto a seleção manual
        if (clinicasData.length === 0 && selectedEstado && !forceClinicaId) {
          import('@/lib/clarity').then(({ trackClarityEvent, setClarityTag }) => {
            setClarityTag('search_estado', selectedEstado)
            if (selectedCidade) setClarityTag('search_cidade', selectedCidade)
            if (selectedBairro) setClarityTag('search_bairro', selectedBairro)
            trackClarityEvent('clinica_search_empty_result')
          })
        }
      } catch (error: any) {
        console.error('Erro ao carregar clínicas:', error)

        // Melhor tratamento de erro
        let errorMessage = 'Erro ao carregar clínicas'

        if (error?.message) {
          errorMessage = error.message
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error?.response?.data?.error) {
          errorMessage = error.response.data.error
        } else if (typeof error === 'string') {
          errorMessage = error
        }

        // Formatar mensagem
        errorMessage = String(errorMessage || 'Erro desconhecido')
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .replace(/\s+/g, ' ')
          .trim()

        setError(errorMessage)
      } finally {
        setLoading(prev => ({ ...prev, clinicas: false }))
      }
    }

    // Debounce para evitar muitas requisições
    const timeoutId = setTimeout(loadClinicas, 300)
    return () => clearTimeout(timeoutId)
  }, [selectedEstado, selectedCidade, selectedBairro, forceClinicaId, modeloExame])

  // Reset selections quando estado muda
  const handleEstadoChange = (estado: string) => {
    setSelectedEstado(estado)
    setSelectedCidade('')
    setSelectedBairro('')
    setSelectedClinicaId(null)
    setClinicas([])
    setError('')
    setFiltrosInicializados(true)
  }

  // Reset selections quando cidade muda
  const handleCidadeChange = (cidade: string) => {
    setSelectedCidade(cidade)
    // Se cidade for limpa, também limpar bairro
    if (!cidade) {
      setSelectedBairro('')
    }
    setSelectedClinicaId(null)
    setFiltrosInicializados(true)
  }

  // Reset selections quando bairro muda
  const handleBairroChange = (bairro: string) => {
    setSelectedBairro(bairro)
    setSelectedClinicaId(null)
    setFiltrosInicializados(true)
  }

  // Selecionar clínica
  const handleClinicaSelect = (clinica: ClinicaItem) => {
    setSelectedClinicaId(clinica.id)
    onClinicaSelect?.(clinica)
  }

  // Handler para quando não encontrar resultados na busca
  // Dispara evento 'clinicaSearchNoResults' com todos os dados dos filtros
  // Exemplo de uso para analytics:
  // window.addEventListener('clinicaSearchNoResults', (e: Event) => {
  //   const customEvent = e as CustomEvent
  //   const { searchTerm, field, currentValue, estado, cidade, bairro, timestamp } = customEvent.detail
  //   // Enviar para seu serviço de analytics
  //   analytics.track('clinica_search_no_results', {
  //     searchTerm,
  //     field,
  //     currentValue,
  //     estado,
  //     cidade,
  //     bairro,
  //     timestamp
  //   })
  // })
  const handleNoResults = (data: {
    searchTerm: string
    field: string
    currentValue: string
    allFilters: Record<string, string>
  }) => {
    // Disparar evento customizado para analytics
    const event = new CustomEvent('clinicaSearchNoResults', {
      detail: {
        searchTerm: data.searchTerm,
        field: data.field,
        currentValue: data.currentValue,
        estado: data.allFilters.estado || selectedEstado,
        cidade: data.allFilters.cidade || selectedCidade,
        bairro: data.allFilters.bairro || selectedBairro,
        timestamp: new Date().toISOString()
      }
    })
    window.dispatchEvent(event)

    // Disparo imediato para o Clarity
    trackClarityEvent('clinica_search_not_found_typing', {
      searchTerm: data.searchTerm,
      field: data.field,
      estado: data.allFilters.estado || selectedEstado,
      cidade: data.allFilters.cidade || selectedCidade,
      bairro: data.allFilters.bairro || selectedBairro
    })

    // Também logar no console para debug (pode remover depois)
    console.log('🔍 Busca sem resultados:', event.detail)
  }

  // Obter todos os filtros atuais
  const getAllFilters = () => ({
    estado: selectedEstado,
    cidade: selectedCidade,
    bairro: selectedBairro
  })

  // Disparar evento quando busca de clínica não encontrar resultados
  // Dispara evento 'clinicaSearchNoResults' com dados dos filtros
  // Exemplo de uso para analytics:
  // window.addEventListener('clinicaSearchNoResults', (e: Event) => {
  //   const customEvent = e as CustomEvent
  //   const { searchTerm, field, estado, cidade, bairro, timestamp } = customEvent.detail
  //   // Verificar se é busca de clínica (field === 'clinica') ou filtro (estado, cidade, bairro)
  //   if (field === 'clinica') {
  //     analytics.track('clinica_search_no_results', {
  //       searchTerm,
  //       estado,
  //       cidade,
  //       bairro,
  //       timestamp
  //     })
  //   }
  // })
  const previousClinicaSearchRef = useRef<string>('')
  useEffect(() => {
    if (searchTerm.length > 0 && clinicas.length > 0) {
      const filteredClinicas = clinicas.filter((clinica) => {
        const search = searchTerm.toLowerCase()
        return (
          clinica.nome.toLowerCase().includes(search) ||
          (clinica.bairro || '').toLowerCase().includes(search) ||
          clinica.cidade.toLowerCase().includes(search)
        )
      })

      // Se não encontrou resultados e o termo mudou
      if (filteredClinicas.length === 0 && searchTerm !== previousClinicaSearchRef.current) {
        previousClinicaSearchRef.current = searchTerm

        const event = new CustomEvent('clinicaSearchNoResults', {
          detail: {
            searchTerm,
            field: 'clinica',
            currentValue: '',
            estado: selectedEstado,
            cidade: selectedCidade,
            bairro: selectedBairro,
            timestamp: new Date().toISOString()
          }
        })
        window.dispatchEvent(event)
        
        // Disparo imediato para o Clarity
        import('@/lib/clarity').then(({ trackClarityEvent, setClarityTag }) => {
            setClarityTag('search_term', searchTerm)
            setClarityTag('search_estado', selectedEstado)
            if (selectedCidade) setClarityTag('search_cidade', selectedCidade)
            if (selectedBairro) setClarityTag('search_bairro', selectedBairro)
            trackClarityEvent('clinica_search_not_found_local_typing')
        })

        console.log('🔍 Busca de clínica sem resultados:', event.detail)
      }
    } else if (searchTerm.length === 0) {
      previousClinicaSearchRef.current = ''
    }
  }, [searchTerm, clinicas, selectedEstado, selectedCidade, selectedBairro])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Buscar Clínica
        </h3>
        {clinicas.length > 0 && (
          <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
            {clinicas.length} clínica(s) encontrada(s)
          </span>
        )}
      </div>

      {error && (
        <FormMessage
          type="error"
          message={error}
          onClose={() => setError('')}
        />
      )}


      {/* Filtros em Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Estado */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Estado *
          </label>
          <SearchableSelect
            options={estados}
            value={selectedEstado}
            onChange={handleEstadoChange}
            getOptionValue={(estado) => estado.uf}
            getOptionLabel={(estado) => `${estado.uf} - ${estado.nome}`}
            placeholder="Selecione"
            disabled={disabled || loading.estados}
            loading={loading.estados}
            emptyMessage="Nenhum estado encontrado"
            onNoResults={handleNoResults}
            fieldName="estado"
            allFilters={getAllFilters()}
          />
        </div>

        {/* Cidade */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cidade
          </label>
          <SearchableSelect
            options={cidades}
            value={selectedCidade}
            onChange={handleCidadeChange}
            getOptionValue={(cidade) => cidade?.cidade || ''}
            getOptionLabel={(cidade) => cidade?.cidade || ''}
            placeholder="Todas"
            disabled={disabled || !selectedEstado || loading.cidades}
            loading={loading.cidades}
            emptyMessage="Nenhuma cidade encontrada"
            onNoResults={handleNoResults}
            fieldName="cidade"
            allFilters={getAllFilters()}
            allowClear={true}
          />
        </div>

        {/* Bairro */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bairro (Opcional)
          </label>
          <SearchableSelect
            options={bairros}
            value={selectedBairro}
            onChange={handleBairroChange}
            getOptionValue={(bairro) => (bairro as any).nome || bairro.bairro}
            getOptionLabel={(bairro) => (bairro as any).nome || bairro.bairro}
            placeholder="Todos"
            disabled={disabled || !selectedCidade || loading.bairros}
            loading={loading.bairros}
            emptyMessage="Nenhum bairro encontrado"
            onNoResults={handleNoResults}
            fieldName="bairro"
            allFilters={getAllFilters()}
            allowClear={true}
          />
        </div>
      </div>

      {/* Busca Local */}
      {clinicas.length > 0 && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Buscar na lista
          </label>
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Digite o nome ou endereço da clínica..."
              className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Lista de Clínicas em Grid */}
      {(selectedEstado || forceClinicaId || modeloExame === '1') && (
        <div className="mt-6">
          {loading.clinicas ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <p className="mt-2 text-gray-600">Buscando clínicas...</p>
            </div>
          ) : clinicas.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200 text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p className="mt-2">Nenhuma clínica encontrada para os filtros selecionados.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[500px] overflow-y-auto pr-2">
              {clinicas
                .filter((clinica) => {
                  if (!searchTerm) return true
                  const search = searchTerm.toLowerCase()
                  return (
                    clinica.nome.toLowerCase().includes(search) ||
                    (clinica.bairro || '').toLowerCase().includes(search) ||
                    clinica.cidade.toLowerCase().includes(search)
                  )
                })
                .map((clinica) => {
                  const isSelected = selectedClinicaId === clinica.id
                  return (
                    <div
                      key={clinica.id}
                      onClick={() => handleClinicaSelect(clinica)}
                      className={`
                      relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 group
                      ${isSelected
                          ? 'border-green-500 bg-green-50 shadow-md'
                          : 'border-gray-200 hover:border-green-300 hover:shadow-sm bg-white'
                        }
                    `}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className={`font-bold text-lg ${isSelected ? 'text-green-900' : 'text-gray-900'}`}>
                              {clinica.nome}
                            </h4>
                            {isSelected && (
                              <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full font-medium">
                                Selecionada
                              </span>
                            )}
                          </div>

                          <div className="space-y-1 text-sm text-gray-600">
                            <p className="flex items-start gap-2">
                              <svg className="w-4 h-4 mt-0.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span>
                                {clinica.bairro}, {clinica.cidade} - {clinica.uf}
                              </span>
                            </p>
                          </div>

                          {/* Horários de funcionamento - sempre exibir, filtrados por tipo */}
                          {(() => {
                            // Filtrar horários baseado no tipo (presencial/online)
                            const isOnline = modeloExame === '1'
                            const horariosFiltrados = clinica.horarios?.filter((horario: any) => {
                              if (isOnline) {
                                return horario.tipo === 'online'
                              } else {
                                // Presencial: mostrar apenas horários presenciais ou null (retrocompatibilidade)
                                return horario.tipo === 'presencial' || horario.tipo === null || horario.tipo === undefined
                              }
                            }) || []

                            if (horariosFiltrados.length === 0) return null

                            return (
                              <div className={`mt-3 pt-3 border-t ${isSelected ? 'border-green-200' : 'border-gray-200'}`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <svg className={`w-4 h-4 ${isSelected ? 'text-green-600' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span className={`text-sm font-medium ${isSelected ? 'text-green-800' : 'text-gray-700'}`}>
                                    Horários de funcionamento {isOnline ? '(Online)' : '(Presencial)'}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-1 text-xs">
                                  {horariosFiltrados.map((horario: any) => {
                                    const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
                                    const diaNome = diasSemana[horario.dia] || `Dia ${horario.dia}`;

                                    return (
                                      <div key={horario.dia} className="flex justify-between items-center">
                                        <span className="text-gray-600">{diaNome}:</span>
                                        <span className="font-medium text-gray-900">
                                          {horario.de && horario.ate ? `${horario.de}-${horario.ate}` : 'Fechado'}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )
                          })()}
                        </div>

                        <div className={`
                        w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-3 transition-colors
                        ${isSelected
                            ? 'bg-green-500 border-green-500 text-white'
                            : 'border-gray-300 group-hover:border-green-400'
                          }
                      `}>
                          {isSelected && (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
            </div>
          )}
        </div>
      )}

      {/* Os horários agora são mostrados dentro do próprio card selecionado */}
    </div>
  )
}
